import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AuthenticationService } from '../services/authentication.service';
import { Router } from '@angular/router';
import { PpsUtilityService } from '../services/pps-utility.service';
import { BusinessOperation } from '../shared/models/business-operation';
import { ReportService } from '../services/report.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { LogoutReason } from '../shared/models/login-change-model';
import { LoggingService } from '../services/logging.service';

import { MenuItem } from 'primeng/api';
import { SharedModule } from 'primeng/api';
import { SubSink } from 'subsink';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Menubar } from 'primeng/menubar';
import { Sidebar } from 'primeng/sidebar';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'pps-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class NavMenuComponent implements OnInit {
  smallScreen: boolean = false;
  private subs = new SubSink();

  menuItems: MenuItem[] = [];
  isMenuActivated: boolean = true;
  @ViewChild('menu') menubar!: Menubar;
  
  @ViewChild('sidebarRef') sidebarRef!: Sidebar;

  isLoggedIn: boolean = false;
  isMenuCollapsed: boolean = true;
  title = 'pps-client';
  generatingReport: boolean = false;  
  userProfileItems: MenuItem[] = [];
  showProfileSideBar: boolean = false;

  clientName: string = environment.clientName;
  clientUrl: string = environment.clientUrl;
  clientLogo: string = environment.clientLogo;

  constructor(
    private _authService: AuthenticationService,
    private _util: PpsUtilityService,
    private _reportService: ReportService,
    private _spinner: NgxSpinnerService,
    private _router: Router,
    private _logger: LoggingService,
    private breakpointObserver: BreakpointObserver) {
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });

    this._authService.loginChanged.subscribe(loggedIn => {
      this.isLoggedIn = loggedIn;
      if (this.isLoggedIn){
        this.getMenuItems();
      }
    });
  }

  ngOnInit(): void {
    this._authService.userLoggedIn().then(loggedIn => {
      this.isLoggedIn = loggedIn;
      if (this.isLoggedIn){
        this.getMenuItems();
      }
    });
  }

  // 1. Triggered on first click: turns on hover expansion for other items
  activateHoverSwitching() {
    this.isMenuActivated = true;
  }

  // 2. Triggered when cursor leaves menubar container entirely
  deactivateHoverSwitching() {
    this.isMenuActivated = false;
    
    // Explicitly hide any open submenus to prevent floaters
    if (this.menubar) {
      this.menubar.hide();
    }
  }
    
  login(){
    this._authService.login();
  }

  logout(){
    this._authService.logout();
  }

  isActiveRoute(router: string){
    let currentRoute = this._router.url;
    currentRoute = currentRoute === "/" ? "/home" : currentRoute;

    return currentRoute === router;
  }

  get isLocalOrDevelopment(): boolean{
    return this._util.isLocalOrDevelopment;
  }
  
  get isTest(): boolean{
    return this._util.isTest;
  }
  
  get isProduction(): boolean{
    return this._util.isProduction;
  }
  get isUserAdmin(){
    return this._authService.isUserAdmin;
  }

  get isUserSysAdmin(){
    return this._authService.isUserSysAdmin;
  }
  
  get userFullname(){
    return this._authService.userFullname;
  }

  get userContext(){
    return this._authService.userContext;
  }
  
  getMenuBarStyle(){
    return this.isLocalOrDevelopment ? "p-menubar-dev" : this.isTest ? "p-menubar-test" : "p-menubar";
  }

  canViewLinks(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewLinks,
      BusinessOperation.ManageLinks
    ]);
  }

  canManageLinks(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ManageLinks
    ]);
  }

  canManageRosterCodes(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ManageRosterCode
    ]);
  }

  canManageHoliday(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ManageHoliday
    ]);
  }

  canManageEmployer(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ManageEmployer
    ]);
  }

  canManageRosterSettings(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ManageRosterSetting
    ]);
  }

  canManageTrainingCourses(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ManageTrainingCourse
    ]);
  }

  canViewProject(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewProject,
      BusinessOperation.CreateProject,
      BusinessOperation.ModifyProject,
      BusinessOperation.DeleteProject,
      BusinessOperation.ExportProject
    ]);
  }

  canViewTimeSheet(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewTimeSheet,
      BusinessOperation.EnterTimeSheet,
      BusinessOperation.ModifyTimeSheet,
      BusinessOperation.UpdatePastTimeSheet,
      BusinessOperation.ExportTimeSheet
    ]);
  }

  canViewRoster(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewRoster,
      BusinessOperation.ModifyRoster
    ]);
  }

  canViewEmployee(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewEmployee,
      BusinessOperation.AddEmployee,
      BusinessOperation.ModifyEmployee,
      BusinessOperation.ExportEmployee
    ]);
  }

  canViewEmployeeTrainingRecord(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ManageEmployeeTrainingRecord
    ]);
  }

  canViewPayrollTimesheet(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewPayrollTimesheet,
      BusinessOperation.ExportPayrollTimesheet
    ]);
  }

  canDownloadReport(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.GenerateMonthlyIncomeReport,
      BusinessOperation.ExportPoInvoiceDiscrepancyReport
    ]);
  }

  canExportPoInvoiceDiscrepanctReport(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ExportPoInvoiceDiscrepancyReport);
  }

  canGenerateMonthlyIncomeReport(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.GenerateMonthlyIncomeReport);
  }

  canManageSystemSettings(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      //BusinessOperation.ManageArea,
      BusinessOperation.ManageClientEngineer,
      BusinessOperation.ManageWorkGroup,
      BusinessOperation.ManageJobPosition,
      BusinessOperation.ManagePayCategory,
      BusinessOperation.ManageLinks,
      BusinessOperation.ManageTimesheetBreakDownRule
    ]);
  }

  canManageClientEngineer(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageClientEngineer);
  }

  canManageWorkGroup(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageWorkGroup);
  }

  canManageJobPosition(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageJobPosition);
  }

  canManagePayCategory(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManagePayCategory);
  }

  canManageTimesheetBreakDownRule(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageTimesheetBreakDownRule);
  }

  canManageAccount(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageAllAccount);
  }

  canManageProjectColumn(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageProjectColumn);
  }

  canViewOpenProject(): boolean{
    return this._authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewOpenProject,
      BusinessOperation.ModifyOpenProject
    ]);
  }

  poInvoiceDiscrepancyReport(){
    this.generatingReport = true;
    this._spinner.show("reportSpinner");
    this._reportService.generatePoInvoiceDiscrepancyReport(
      () => {
        this.generatingReport = false;
        this._spinner.hide("reportSpinner");
      });
  }

  generateMonthlyIncomeReport(){
    this.generatingReport = true;
    this._spinner.show("reportSpinner");
    this._reportService.generateMonthlyIncomeReport(
      () => {
        this.generatingReport = false;
        this._spinner.hide("reportSpinner");
      });
  }  

  getUserFullname(){
    return this._authService.userFullname;
  }

  getUserInitial(){
    let firstname = this._authService.userContext.zoesenUser.firstName;
    let lastname = this._authService.userContext.zoesenUser.lastName;

    let initial = (firstname ? firstname.substr(0, 1).toUpperCase() : "") +
      (lastname ? lastname.substr(0, 1).toUpperCase() : "");

    return initial;
  }

  getUserEmail(){
    return this._authService.userContext.zoesenUser.email;
  }


  getMenuItems(){
    this.menuItems = [
      {
        label: "Home",
        icon: "pi-fw fas fa-home",
        routerLink: ['/home']
      }
    ];

    this.addViewLinksMenu();
    this.addProjectMenu();
    this.addRosterMenu();
    this.addTimesheetMenu();
    this.addEmployeeMenu();
    this.addPayrollMenu();
    this.addReportsMenu();
    this.addSystemSettingsMenu();
    this.addAdminMenu();
    this.addUserProfileMenu();
  }

  private addViewLinksMenu(){
    if (this.canViewLinks()){
      let linksMenu = {
        label: "Links",
        icon: "pi-fw fas fa-link",
        items: []
      };
      this.userContext.externalLinks.forEach(link => {
        linksMenu.items.push({
          label: link.name,
          icon: "pi-fw fas fa-external-link-alt",
          url: link.url,
          target: "_blank"
        });
      })

      this.menuItems.push(linksMenu);
    }
  }

  private addProjectMenu(){
    if (this.canViewProject()){
      if (this.canViewOpenProject()){
        let menu = {
          label: "Project",
          icon: "pi-fw fas fa-tractor",
          items: []
        };

        menu.items.push({
          label: "Project",
          icon: "pi-fw fas fa-tractor",
          routerLink:['/projects']
        });

        menu.items.push({
          label: "Open Project",
          icon: "pi-fw fas fa-tasks",
          routerLink:['/open-project']
        });
        
        this.menuItems.push(menu);
      }
      else{
        let menu = {
          label: "Project",
          icon: "pi-fw fas fa-tractor",
          routerLink:['/projects']
        };
        
        this.menuItems.push(menu);
      }
    }
  }

  private addTimesheetMenu(){
    if (this.canViewTimeSheet()){
      let menu = {
        label: "Timesheet",
        icon: "pi-fw fas fa-user-clock",
        routerLink: ['/timesheets']
      };
      this.menuItems.push(menu);
    }
  }

  private addEmployeeMenu(){
    if (this.canViewEmployee()){
      if (this.canViewEmployeeTrainingRecord()){
        let menu = {
          label: "Employee",
          icon: "pi-fw far fa-address-card",
          items: []
        };

        menu.items.push({
          label: "Employee",
          icon: "pi-fw far fa-address-card",
          routerLink: ['/employees']
        });

        menu.items.push({
          label: "Employee Training Record",
          icon: "pi-fw fas fa-graduation-cap",
          routerLink: ['/employee-training-record']
        });

        this.menuItems.push(menu);
      }else{
        let menu = {
          label: "Employee",
          icon: "pi-fw far fa-address-card",
          routerLink: ['/employees']
        };

        this.menuItems.push(menu);
      }
    }
  }

  private addPayrollMenu(){
    if (this.canViewPayrollTimesheet()){
      let menu = {
        label: "Payroll",
        icon: "pi-fw fas fa-hand-holding-usd",
        routerLink: ['/payroll-timesheet']
      };
      this.menuItems.push(menu);
    }
  }

  private addRosterMenu(){
    if (this.canViewRoster() && this.userContext.rosterProjects.length > 0){
      let menu = {
        label: "Roster",
        icon: "pi-fw fas fa-calendar-alt",
        items: []
      };
      this.userContext.rosterProjects.forEach(rp => {
        var menuItem = {
          label: `${rp.title}`,
          icon: "pi-fw fas fa-calendar-alt fa-lg",
          items: [
            {
              label: "Active Roster",
              icon: "pi-fw far fa-calendar-check text-danger fa-lg",
              routerLink: [`/roster`],
              queryParams: {'roster-project': rp.id, 'active-roster': 'true'}
            },
            {
              label: "Inactive Roster",
              icon: "pi-fw far fa-calendar-times fa-lg",
              routerLink: [`/roster`],
              queryParams: {'roster-project': rp.id, 'active-roster': 'false'}
            }
          ]
        };

        menu.items.push(menuItem);
      });

      this.menuItems.push(menu);
    }
  }

  private addReportsMenu(){
    if (this.canDownloadReport()){
      let menu = {
        label: "Report",
        icon: "pi-fw fas fa-table",
        items: []
      };

      if (this.canExportPoInvoiceDiscrepanctReport()){
        menu.items.push({
          label: 'IPV Report',
          icon: "pi-fw far fa-file-excel fa-lg text-success ",
          routerLink: ['/po-invoice-discrepancy-report']
        });
      }

      if (this.canGenerateMonthlyIncomeReport()){
        menu.items.push({
          label: 'Monthly Income Report',
          icon: "pi-fw far fa-file-excel fa-lg text-success",
          routerLink: ['/monthly-income-report']
        });
      }

      this.menuItems.push(menu);
    }
  }

  private addSystemSettingsMenu(){
    if (this.canManageSystemSettings()){
      let menu = {
        label: "Settings",
        icon: "pi-fw fas fa-tools",
        items: []
      };

      if (this.canManageEmployer()){
        menu.items.push({
          label: 'Manage Company',
          icon: "pi-fw fas fa-city fa-lg",
          routerLink: ['/manage-company']
        });
      }

      if (this.canManageClientEngineer()){
        menu.items.push({
          label: 'Manage Client',
          icon: "pi-fw fas fa-user-cog fa-lg",
          routerLink: ['/client-management']
        });
      }

      if (this.canManageWorkGroup()){
        menu.items.push({
          label: 'Manage Work Group',
          icon: "pi-fw fas fa-user-friends fa-lg",
          routerLink: ['/system-data-management'],
          queryParams: { entity: 'Work Group'}
        });
      }
      
      if (this.canManageJobPosition()){
        menu.items.push({
          label: 'Manage Job Position',
          icon: "pi-fw fas fa-address-card fa-lg",
          routerLink: ['/system-data-management'],
          queryParams: { entity: 'Position'}
        });
      }

      if (this.canManagePayCategory()){
        menu.items.push({
          label: 'Manage Pay Category',
          icon: "pi-fw fas fa-money-check-alt fa-lg",
          routerLink: ['/system-data-management'],
          queryParams: { entity: 'Pay Category'}
        });
      }

      if (this.canManageTimesheetBreakDownRule()){
        menu.items.push({
          label: 'Manage Payroll Timesheet Rules',
          icon: "pi-fw fas fa-comment-dollar fa-lg",
          routerLink: ['/timesheet-breakdown-rule']
        });
      }

      if (this.canManageRosterCodes()){
        menu.items.push({
          label: 'Manage Roster Codes',
          icon: "pi-fw far fa-keyboard fa-lg",
          routerLink: ['/manage-roster-code']
        });
      }

      if (this.canManageHoliday()){
        menu.items.push({
          label: 'Manage Public Holiday',
          icon: "pi-fw fas fa-umbrella-beach fa-lg",
          routerLink: ['/manage-holiday']
        });
      }

      if (this.canManageLinks()){
        menu.items.push({
          label: 'Manage Links',
          icon: "pi-fw fas fa-link fa-lg",
          routerLink: ['/manage-resource']
        });
      }

      if (this.canManageRosterSettings()){
        menu.items.push({
          label: 'Manage Roster Settings',
          icon: "pi-fw fas fa-cog fa-lg",
          routerLink: ['/manage-roster-settings']
        });
      }

      if (this.canManageTrainingCourses()){
        menu.items.push({
          label: 'Manage Training Courses',
          icon: "pi-fw fa fa-lg fa-graduation-cap",
          routerLink: ['/manage-training-courses']
        });
      }

      if (this.canManageProjectColumn()){
        menu.items.push({
          label: 'Manage Project Columns',
          icon: "pi-fw fas fa-columns fa-lg",
          routerLink: ['/manage-project-columns']
        });
      }

      if (menu.items.length > 0 ){
        this.menuItems.push(menu);
      }
    }
  }

  private addAdminMenu(){
    let menu: MenuItem = {
      label: "Admin",
      icon: "pi-fw fas fa-user-lock",
      items: []
    };

    if (this.isUserAdmin || this.isUserSysAdmin){
      menu.items.push({
        label: 'Role Users',
        icon: "pi-fw fas fa-users fa-lg",
        routerLink: ['/user-role-management']
      });
      menu.items.push({
        label: 'Role Permissions',
        icon: "pi-fw fas fa-user-clock fa-lg",
        routerLink: ['/role-operation-management']
      });
      menu.items.push({
        label: 'Project Column Access Settings',
        icon: "pi-fw fas fa-clipboard-list fa-lg",
        routerLink: ['/project-column-access-setting']
      });
    }

    if (this.canManageAccount()){
      menu.items.push({
        label: 'Manage Users',
        icon: "pi-fw far fa-user-circle fa-lg",
        routerLink: ['/manage-user']
      });
    }

    // if (this.isUserSysAdmin){
    //   menu.items.push({
    //     label: 'Supporting Data Management',
    //     icon: "pi-fw fas fa-database fa-lg",
    //     routerLink: ['/data-import']
    //   });

    //   menu.items.push({
    //     label: 'Import Excel Roster',
    //     icon: "pi-fw fas fa-file-import fa-lg",
    //     routerLink: ['/import-roster']
    //   });
    // }

    if (menu.items.length > 0 ){
      this.menuItems.push(menu);
    }
  }

  private addUserProfileMenu(){
    this.userProfileItems = [
        {
          label: "Manage your profile",
          icon: "pi pi-fw pi-user fa-lg",
          command: () => this.gotoUserProfile()
        },
        {
          label: "Update password",
          icon: "pi pi-fw pi-lock fa-lg",
          command: () => this.updatePassword()
        },
        {
          label: "Sign out",
          icon: "pi pi-fw pi-sign-out fa-lg",
          command: () => this._authService.logout()
        }
    ];
  }

  private gotoUserProfile(){
    this._router.navigate(["/user-profile"], { replaceUrl: true, queryParams: {section: "profile"}});   
    this.showProfileSideBar = false;
  }

  private updatePassword(){
    this._router.navigate(["/user-profile"], { replaceUrl: true, queryParams: {section: "cred"}});   
    this.showProfileSideBar = false;
  }

  handleSidebarHide(){
    // A 0ms timeout defers execution until the animation loop finishes
    setTimeout(() => {
      if (this.sidebarRef) {
        // 1. Tells PrimeNG to clear out its internal template mask pointer
        this.sidebarRef.destroyModal(); 
        
        // 2. Fallback step to clear any orphan mask nodes missed by the framework
        const orphanMasks = document.querySelectorAll('.p-sidebar-mask');
        orphanMasks.forEach(mask => mask.remove());
        
        // 3. Unlocks background scrolling capacity on the parent window layout
        document.body.classList.remove('p-overflow-hidden');
      }
    }, 0);
  }
}

